import { applicationServerV5Root, CLIENT_HEADERS } from 'library/helpers/superFetch';
import { getAuthToken } from 'library/auth/authProvider';

/**
 * Is the signed-in user a Zezamii partner, and which customer orgs are in their partner scope?
 *
 * WHY THIS BYPASSES SuperFetch. Partner identity is USER-scoped — a `PartnerMembership` is keyed on
 * (ProviderID, Subject) and spans every org — so the question is answered by the ORG-LESS boundary
 * `GET /v5/partner-portal/overview`. SuperFetch pins an orgID into every URL by construction, so it
 * cannot express that call. Same reason `Redux/auth/saga.js` hand-rolls its token exchange.
 *
 * WHY NOT THE PER-ORG `/partner/context` THE LAUNCHER USED TO CALL. That surface sits behind
 * `requireFeature('partnerPortal')` on `/v5/:orgID/partner`, a PER-ORG flag. So the Partner tile could
 * only ever appear in an org that had been individually flagged — which on production meant exactly one
 * org. A partner switching into a customer they service, or into their own partner workspace, saw
 * nothing. Reading the org-less boundary instead makes the tile follow the PERSON, which is what Partner
 * always was (it is appended outside `selectActiveModules` for the same reason).
 *
 * FAIL-CLOSED. Anything other than a 200 carrying a partner — 403 not-a-partner, 503 rollup disabled,
 * a network error — resolves to `null`, i.e. "not a partner, show no tile". A partner who is briefly
 * invisible is a missing shortcut to another site; a non-partner who is briefly visible is a wrong claim
 * about who they are.
 *
 * @returns {Promise<{partner: Object, orgs: Array}|null>}
 */
export async function fetchPartnerIdentity() {
	try {
		const token = await getAuthToken();
		if (!token) return null;

		const response = await fetch(`${applicationServerV5Root()}/partner-portal/overview`, {
			method: 'GET',
			headers: {
				'Content-Type': 'application/json',
				'Accept': 'application/json',
				...CLIENT_HEADERS,
				'Authorization': `Bearer ${token}`
			},
			cache: 'no-store'
		});
		// 403 = not a partner (the common case for almost every user), 503 = rollup kill-switch off.
		// Neither is an error worth surfacing: the tile simply does not appear.
		if (!response.ok) return null;

		const res = await response.json();
		return res?.data?.partner ? { partner: res.data.partner, orgs: res.data.orgs || [] } : null;
	} catch {
		return null;
	}
}

export default fetchPartnerIdentity;
